// 月のリズム：月齢・月の形・次の新月／満月までの日数（端末内計算）
import { MoonPhase, SearchMoonPhase } from 'astronomy-engine';

export type MoonPhaseKey =
  | 'new' | 'crescent' | 'waxing' | 'firstQuarter' | 'gibbous'
  | 'full' | 'izayoi' | 'waning' | 'lastQuarter' | 'ariake';

export type MoonInfo = {
  age: number;              // 月齢（直前の新月からの経過日数）
  phaseAngle: number;       // 黄経差 0〜360（0=新月, 180=満月）
  phaseKey: MoonPhaseKey;
  phaseName: string;        // 例：上弦の月
  daysToNew: number;        // 次の新月まで（暦日。今日なら0）
  daysToFull: number;       // 次の満月まで（暦日。今日なら0）
  nextNew: Date;
  nextFull: Date;
};

const DAY_MS = 86400000;

/** 端末のローカル暦日で、a から b まで何日か */
function calendarDaysBetween(a: Date, b: Date): number {
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((db - da) / DAY_MS);
}

/** 今日（ローカル暦日）中に指定の月相（黄経差）が起きるか */
function happensToday(targetLon: number, now: Date): boolean {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const t = SearchMoonPhase(targetLon, start, 1);
  return !!t && calendarDaysBetween(now, t.date) === 0;
}

const PHASE_NAMES: Record<MoonPhaseKey, string> = {
  new: '新月',
  crescent: '三日月',
  waxing: '上弦に向かう月',
  firstQuarter: '上弦の月',
  gibbous: '十三夜月',
  full: '満月',
  izayoi: '十六夜の月',
  waning: '下弦に向かう月',
  lastQuarter: '下弦の月',
  ariake: '有明の月',
};

function classify(angle: number, age: number, now: Date): MoonPhaseKey {
  // 主要な月相は「その日に起きる」場合だけ名乗る（前後数日で同じ名前が続かないように）
  if (happensToday(0, now)) return 'new';
  if (happensToday(90, now)) return 'firstQuarter';
  if (happensToday(180, now)) return 'full';
  if (happensToday(270, now)) return 'lastQuarter';
  if (angle < 90) return age < 4.5 ? 'crescent' : 'waxing';
  if (angle < 180) return age >= 11.5 ? 'gibbous' : 'waxing';
  if (angle < 270) return age < 17 ? 'izayoi' : 'waning';
  return 'ariake';
}

export function getMoonInfo(now: Date = new Date()): MoonInfo {
  const phaseAngle = MoonPhase(now);
  const prevNew = SearchMoonPhase(0, new Date(now.getTime() - 31 * DAY_MS), 31);
  // 直前の新月：31日前から探して見つかった新月が now を超えないよう、最後のものを選ぶ
  let lastNewDate = prevNew ? prevNew.date : new Date(now.getTime() - (phaseAngle / 360) * 29.53 * DAY_MS);
  while (true) {
    const next = SearchMoonPhase(0, new Date(lastNewDate.getTime() + DAY_MS), 31);
    if (!next || next.date.getTime() > now.getTime()) break;
    lastNewDate = next.date;
  }
  const age = Math.max(0, (now.getTime() - lastNewDate.getTime()) / DAY_MS);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nextNewT = SearchMoonPhase(0, startOfToday, 40);
  const nextFullT = SearchMoonPhase(180, startOfToday, 40);
  const nextNew = nextNewT ? nextNewT.date : new Date(now.getTime() + 29.53 * DAY_MS);
  const nextFull = nextFullT ? nextFullT.date : new Date(now.getTime() + 14.77 * DAY_MS);

  const phaseKey = classify(phaseAngle, age, now);
  return {
    age: Math.round(age * 10) / 10,
    phaseAngle,
    phaseKey,
    phaseName: PHASE_NAMES[phaseKey],
    daysToNew: calendarDaysBetween(now, nextNew),
    daysToFull: calendarDaysBetween(now, nextFull),
    nextNew,
    nextFull,
  };
}
